import { useEffect, useRef } from 'react';
import { useToast } from './ToastProvider';

const MESSAGE_COOLDOWN_MS = 2000;

export const ErrorAlertToastBridge = () => {
  const { showErrorToast } = useToast();
  const visibleMessagesRef = useRef<Set<string>>(new Set());
  const recentToastAtRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const scanAndToastErrors = () => {
      const now = Date.now();
      const nodes = Array.from(document.querySelectorAll('[role="alert"]'));
      const nextVisibleMessages = new Set<string>();

      nodes.forEach((node) => {
        const element = node as HTMLElement;
        if (element.dataset.toastIgnore === 'true') {
          return;
        }
        if (element.closest('[data-toast-root="true"]')) {
          return;
        }
        const message = element.textContent?.trim() ?? '';
        if (message === '') {
          return;
        }
        nextVisibleMessages.add(message);
      });

      nextVisibleMessages.forEach((message) => {
        if (visibleMessagesRef.current.has(message)) {
          return;
        }
        const lastToastAt = recentToastAtRef.current.get(message) ?? 0;
        if (now - lastToastAt < MESSAGE_COOLDOWN_MS) {
          return;
        }
        recentToastAtRef.current.set(message, now);
        showErrorToast(message);
      });

      const nextRecentToastAt = new Map<string, number>();
      recentToastAtRef.current.forEach((lastToastAt, message) => {
        if (now - lastToastAt < MESSAGE_COOLDOWN_MS * 6) {
          nextRecentToastAt.set(message, lastToastAt);
        }
      });
      recentToastAtRef.current = nextRecentToastAt;

      visibleMessagesRef.current = nextVisibleMessages;
    };

    scanAndToastErrors();

    const observer = new MutationObserver(() => {
      scanAndToastErrors();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [showErrorToast]);

  return null;
};
